import React from "react"
import { JsonEditor } from "../../JsonEditor"
import { produce } from "immer"
import axios from "axios"
import { z } from "zod"
import { SearchDropdown } from "../../ui-primitives/search-dropdown"
import { Tabs, TabsList, TabsContent, TabsTrigger } from "../../ui-primitives/tabs"
import { Input } from "../../ui-primitives/input"
import { useQuery } from "@tanstack/react-query"
import { getAuthHeaders } from "../../../api/auth"
import { DownloadIcon } from "@radix-ui/react-icons"
import { Button } from "../../ui-primitives/button"

const SCHEMA_APPS = z.array(
  z.object({
    id: z.literal(null),
    path: z.string(),
    title: z.string(),
    project: z.object({
      id: z.literal(null),
      path: z.string(),
      title: z.string(),
    }),
  })
)

const COMMON_MODIFICATION_SCHEMA = z.object({
  timestamp: z.coerce.date(),
})

const FROM_SAME_PLATFORM_SCHEMA = COMMON_MODIFICATION_SCHEMA.extend({
  type: z.literal('from-same-platform'),
  apiURL: z.string(),
})

const FROM_OTHER_PLATFORM_SCHEMA = COMMON_MODIFICATION_SCHEMA.extend({
  type: z.literal('from-other-platform'),
  apiURL: z.string(),
  appURL: z.string(),
})

const CUSTOM_MODIFICATION_SCHEMA =  COMMON_MODIFICATION_SCHEMA.extend({
  type: z.literal('custom'),
  lastImport: z.optional(z.union([
    FROM_SAME_PLATFORM_SCHEMA,
    FROM_OTHER_PLATFORM_SCHEMA,
  ]))
})

const CONFIGURED_SCHEMA = z.object({
  lastModification: z.union([
    FROM_SAME_PLATFORM_SCHEMA,
    FROM_OTHER_PLATFORM_SCHEMA,
    CUSTOM_MODIFICATION_SCHEMA,
  ]),
})

export const COMPONENT_APP_SCHEMA = z.object({
  title: z.string(),
  componentInputVarNames: z.optional(z.array(z.string())),
  componentOutputVarNames: z.optional(z.array(z.string())),
  parameterValues: z.optional(z.string()),
  triggers: z.optional(z.array(z.object({
    label: z.string(),
    sparqlQuery: z.string(),
    voteForUserActionsDisabledWhenLoading: z.boolean(),
  }))),
  mainArea: z.unknown(), // TODO: too much effort to convert WidgetConfig into a zod schema
})

export type ValidComponentApp = z.infer<typeof COMPONENT_APP_SCHEMA>

const CONFIG_SCHEMA = z.union([
  z.null(),
  CONFIGURED_SCHEMA.extend({type: z.literal('invalid'), appConfig: z.unknown()}),
  CONFIGURED_SCHEMA.extend({type: z.literal('valid'), appConfig: COMPONENT_APP_SCHEMA}),
])

type ImportDef
  = z.infer<typeof FROM_SAME_PLATFORM_SCHEMA>
  | z.infer<typeof FROM_OTHER_PLATFORM_SCHEMA>

type ModificationDef = ImportDef | z.infer<typeof CUSTOM_MODIFICATION_SCHEMA>

export type ImportAppJsonConfig = z.infer<typeof CONFIG_SCHEMA>

export const DEFAULT_IMPORT_APP_JSON_CONFIG: ImportAppJsonConfig = null

export function ImportAppJSON(props: {
  name: string
  value: unknown
  onChange: (value: ImportAppJsonConfig) => void | Promise<void>
}): React.ReactNode {

  const {config, defaultTab} = React.useMemo(() => {

    const config = CONFIG_SCHEMA
      .catch((ctx) => {
        console.error(`could not parse ${JSON.stringify(ctx.input)}`, ctx.error)
        return null
      })
      .parse(props.value)

    let defaultTab: ImportDef['type'] = config?.lastModification.type === 'from-other-platform'
      ? 'from-other-platform'
      : 'from-same-platform'

    return { defaultTab, config }
  }, [props.value])

  const applyWithValidation = (config: unknown, lastModification: ModificationDef) => {
    try {
      const appConfig = COMPONENT_APP_SCHEMA.parse(config)
      props.onChange({type: 'valid', appConfig, lastModification})
    } catch(e) {
      console.error(`could not parse ${JSON.stringify(config)}`, e)
      props.onChange({type: 'invalid', appConfig: config, lastModification})
    }
  }

  const clearURLs = () => {
    if (config == null) return
    if (config.lastModification.type === 'custom') return
    produce(config, draft => {
      draft.lastModification = { type: 'custom', timestamp: new Date() }
    })
  }

  const importFromApiURL = async (lastModification: ImportDef) => {
    const res = await axios.get(lastModification.apiURL)
    applyWithValidation(res.data, lastModification)
  }

  return (
    <div className="p-2 border-neutral-500 border-2 rounded-sm flex flex-col gap-2">

      <Tabs defaultValue={defaultTab}>

        <TabsList>
          <TabsTrigger value="from-same-platform">From current platform</TabsTrigger>
          <TabsTrigger value="from-other-platform">From URL</TabsTrigger>
        </TabsList>

        <TabsContent value="from-same-platform">
          <ImportFromPlatform
            initialApiURL={
              (config && 'apiURL' in config.lastModification)
                ? config?.lastModification.apiURL
                : null
            }
            import={apiURL => importFromApiURL({
              type: 'from-same-platform',
              apiURL, timestamp: new Date(),
            })}
            clearImport={clearURLs}
          />
        </TabsContent>

        <TabsContent value="from-other-platform">
          <ImportFromURL
            initialAppURL={
              (config && 'appURL' in config.lastModification)
                ? config.lastModification.appURL
                : null
            }
            import={(apiURL, appURL) => importFromApiURL({
              type: 'from-other-platform',
              apiURL, appURL, timestamp: new Date(),
            })}
            clearImport={clearURLs}
          />
        </TabsContent>

      </Tabs>

      <JsonEditor
        name={props.name + "-appJSON"}
        value={config?.appConfig}
        onValueChange={(value) => {
          const lastImport = (
            config?.lastModification.type === 'from-other-platform'
            || config?.lastModification.type === 'from-same-platform'
          )
            ? config.lastModification
            : undefined
          applyWithValidation(value, {type: 'custom', timestamp: new Date(), lastImport})
        }}
      />
      <div className="text-right">{getSourceStatement(config?.lastModification)}</div>

    </div>
  )
}

type App = {
  label: string,
  value: string,
}

function ImportFromPlatform(props: {
  initialApiURL: string | null
  import: (apiURL: string) => Promise<void>
  clearImport: () => void
}): React.ReactNode {
  const [selectedURL, setSelectedURL] = React.useState<string | null>(props.initialApiURL ?? null)
  const [search, setSearch] = React.useState<string>('')

  const authHeaders = getAuthHeaders()
  const appsResult = useQuery<App[]>({
    retry: 1,
    retryDelay: 0,
    enabled: Boolean(authHeaders.Authorization),
    queryKey: ["fetch-import-from-platform", authHeaders.Authorization],
    queryFn: async () => {
      const res = await axios.get('/api/list-viewers', {headers: authHeaders})
      const apps = SCHEMA_APPS.parse(res.data)
      return apps.map(app => ({
        label: `${app.project.title}: ${app.title}`,
        value: `/api/projects/${app.project.path}/viewers/${app.path}`,
      }))
    }
  })

  /**
   * Priority:
   * - exact value match (case-sensitive)
   * - exact label match (case-insensitive)
   * - then any keyword match (case-insensitive)
   */
  const filterFn = React.useCallback((value: string, search: string, keywords: string[] = []): number => {
    const label = keywords[0]
    if (value === search) return 1
    if (label && label.toLowerCase() === search.toLowerCase()) return 0.9
    if (keywords.join(' ').toLowerCase().includes(search.toLowerCase())) return 0.5
    return 0
  }, [])

  return (
    <SearchDropdown
      labelConfig={{placeholder: 'select app to import'}}
      highlightWords
      syncSearchWithLabel
      search={search}
      onSearch={setSearch}
      isLoading={appsResult.isLoading}
      options={appsResult.data ?? []}
      onOpenChange={() => appsResult.refetch()}
      getLabelForOptionValue={async (value) => {
        const app = appsResult.data?.find(app => app.value === value)
        if (!app) {
          throw new Error(`no app found for url: ${value}`)
        }
        return app.label
      }}
      value={selectedURL}
      filterFn={filterFn}
      onChange={async (apiURL) => {
        setSelectedURL(apiURL)
        if (apiURL) props.import(apiURL)
        else props.clearImport()
      }}
    />
  )
}

function ImportFromURL(props: {
  initialAppURL: string | null
  import: (apiURL: string, appURL: string) => void | Promise<void>
  clearImport: () => void
}): React.ReactNode {
  const [appURL, setURL] = React.useState<string | null>(props.initialAppURL)
  const apiURL = React.useMemo(() => {
    if (!appURL) return null

    try {
      const parsed = new URL(appURL)
      const [,, project, app] = parsed.pathname.split('/', 4) // expecting `/projects/project/app`
      return `${parsed.origin}/api/projects/${project}/viewers/${app}`
    } catch(e) {
      return null
    }
  }, [appURL])

  const applyInput = () => {
    if (apiURL && appURL) props.import(apiURL, appURL)
    else props.clearImport()
  }

  return (
    <div className="flex flex-row w-full gap-2">
      <Input
        placeholder="provide URL and press enter to import"
        value={appURL || ''}
        onChange={e => setURL(e.target.value)}
        onKeyUp={e => {
          if (e.key === 'Enter') applyInput()
        }}
      />
      <Button variant="secondary" className="px-2" 
        title="import config from provided URL"onClick={applyInput}
      ><DownloadIcon /></Button>
    </div>
  )
}

function getSourceStatement(
  lastModification: undefined | z.infer<typeof CONFIGURED_SCHEMA>['lastModification'],
): null | string {
  if (lastModification == null) return null
  switch (lastModification.type) {
    case "custom":
      return `modified at: ${lastModification.timestamp}`
    case "from-same-platform":
      return `imported at: ${lastModification.timestamp}`
    case "from-other-platform":
      return `imported at: ${lastModification.timestamp}`
  }
  throw new Error('This is unreachable according to TypeScript')
}
